import { useCallback, useState, useEffect, useRef, useContext } from "react";
import { CouponModalIds, GlobalOverlayPriority, OverlayType, ReviewedCart } from "@swiggy-private/cart-types";
import {
    getNoFeeWeekendCouponData,
    getNoFeeWeekendFrequencyCapping,
    isNoFeeWeekendCouponActive,
} from "@cart-client/helpers/NoFeeWeekendCampaign";
import {
    CartOverlaysContext,
    ProceedMode,
    SessionVisibility,
    useLocalStorageBasedFrequencyCapping,
} from "@swiggy-private/checkout-core";
import { LOCAL_STORAGE_KEYS } from "@swiggy-private/core-ui";
import { getRestaurantId, getSessionId } from "@cart-client/helpers/Cart";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { isEmpty } from "@swiggy-private/js-utils";
import { useNoFeeWeekendAnalytics } from "./useNoFeeWeekendAnalytics";
import { NO_FEE_WEEKEND_POPUP_ACTIONS } from "@cart-client/constants/index";

export const useNoFeeWeekend = (cartResponse: ReviewedCart | null) => {
    const { onImpressionNoFeeWeekendPopup, onClickNoFeeWeekendPopup } = useNoFeeWeekendAnalytics();

    const [showNoFeeWeekendPopup, setShowNoFeeWeekendPopup] = useState(false);
    const registerOverlayNoFeeWeekendPopup = useRef(false);
    const hasShownPopup = useRef(false);
    const noFeeWeekendData = getNoFeeWeekendCouponData(cartResponse);

    const { registerOverlays, enabledOverlayId, onCloseOverlay, onCloseOverlayImmediate } =
        useContext(CartOverlaysContext);
    const { togglingDelightPopup } = useContext(UpdateCartContext);

    const { shouldProceed } = useLocalStorageBasedFrequencyCapping({
        key: LOCAL_STORAGE_KEYS.WEEKEND_FEE_POPUP_FREQUENCY_CAPPING_KEY,
        id: getRestaurantId(cartResponse),
        sessionId: getSessionId(),
        maxFrequency: getNoFeeWeekendFrequencyCapping(cartResponse),
        sessionVisibility: SessionVisibility.ONCE_PER_SESSION,
    });

    useEffect(() => {
        if (
            !isEmpty(noFeeWeekendData) &&
            shouldProceed({ mode: ProceedMode.ONLY_CHECK }) &&
            !registerOverlayNoFeeWeekendPopup.current
        ) {
            registerOverlays({
                overlayType: OverlayType.MODAL,
                id: CouponModalIds.NO_FEE_WEEKEND_POPUP,
                priority: GlobalOverlayPriority.NO_FEE_WEEKEND_POPUP,
            });
            registerOverlayNoFeeWeekendPopup.current = true;
        }
    }, [noFeeWeekendData, registerOverlays, shouldProceed]);

    useEffect(() => {
        if (isNoFeeWeekendCouponActive(enabledOverlayId) && !showNoFeeWeekendPopup && !hasShownPopup.current) {
            if (!shouldProceed()) {
                onCloseOverlayImmediate();
                return;
            }
            onImpressionNoFeeWeekendPopup(
                String(cartResponse?.data?.cartId),
                String(cartResponse?.data?.originalCartId),
            );
            setShowNoFeeWeekendPopup(true);
            hasShownPopup.current = true;
            togglingDelightPopup();
        }
    }, [
        enabledOverlayId,
        shouldProceed,
        showNoFeeWeekendPopup,
        togglingDelightPopup,
        onCloseOverlayImmediate,
        onImpressionNoFeeWeekendPopup,
        cartResponse?.data?.cartId,
        cartResponse?.data?.originalCartId,
    ]);

    const onClosePopup = useCallback(() => {
        setShowNoFeeWeekendPopup(false);
        onClickNoFeeWeekendPopup(
            String(cartResponse?.data?.cartId),
            String(cartResponse?.data?.originalCartId),
            NO_FEE_WEEKEND_POPUP_ACTIONS.DISMISS,
        );
        togglingDelightPopup();
        onCloseOverlay();
    }, [
        onCloseOverlay,
        togglingDelightPopup,
        onClickNoFeeWeekendPopup,
        cartResponse?.data?.cartId,
        cartResponse?.data?.originalCartId,
    ]);

    const closeModal = useCallback(() => {
        setShowNoFeeWeekendPopup(false);
        onClickNoFeeWeekendPopup(
            String(cartResponse?.data?.cartId),
            String(cartResponse?.data?.originalCartId),
            NO_FEE_WEEKEND_POPUP_ACTIONS.CLICK_YAY,
        );
        togglingDelightPopup();
        onCloseOverlay();
    }, [
        onCloseOverlay,
        togglingDelightPopup,
        onClickNoFeeWeekendPopup,
        cartResponse?.data?.cartId,
        cartResponse?.data?.originalCartId,
    ]);

    const onAnimationEnd = useCallback(() => {
        setShowNoFeeWeekendPopup(false);
        togglingDelightPopup();
        onCloseOverlay();
    }, [onCloseOverlay, togglingDelightPopup]);

    return {
        noFeeWeekendData,
        showNoFeeWeekendPopup,
        onClosePopup,
        onAnimationEnd,
        closeModal,
    };
};
